/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";

import DSA from "./components/dsa";
import { fft, util as fftUtil } from "fft-js";

// react-chartjs-2 components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SbiHeader from "../../components/SbiHeader";
import { SBI_DARK, SBI_LIGHT } from "assets/theme/base/colors";
import { useBranding, FRESENIUS } from "contextBranding";

// Material Dashboard 3 PRO React context
import { useMaterialUIController } from "context";

// Context
import { useAuthContext } from "contextAuth";

// Connect page components
import ParameterCard from "./components/ParameterCard";
import QConCard from "./components/QConCard";

// EEG Generator (simulated)
import { makeEEGGenerator } from "./function/eeg_generator";
// EEG streaming from Bun
import eegService from "services/eeg";
// Placeholder decoder for streamed EEG packets
import { decodeEegMessage } from "./function/eeg_decoder";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Dark background color for medical device
const deviceBackground = "#0a0a0a";

function Connect() {
  const { t } = useTranslation();
  const { userInfo, logoutUser } = useAuthContext();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { sbiDashboardTheme } = controller;
  const { isFreseniusBranding } = useBranding();
  const isLight = sbiDashboardTheme === "light";
  const theme = isLight ? SBI_LIGHT : SBI_DARK;
  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const contentBg = isFreseniusBranding ? freseniusTheme.bgMain : theme.bgMain;
  const contentColor = isFreseniusBranding ? freseniusTheme.textPrimary : theme.textPrimary;
  const [waveformData, setWaveformData] = useState([]);
  const [lastEegPacket, setLastEegPacket] = useState(null); // decoded EEG data from Bun
  const [sweepDurationSeconds, setSweepDurationSeconds] = useState(10); // Sweep duration: 5 or 10 seconds
  const [wsConnectionState, setWsConnectionState] = useState("disconnected"); // WebSocket connection state
  const [timeWindow, setTimeWindow] = useState({ min: 0, max: 0 }); // Time window for x-axis
  const [trendData, setTrendData] = useState({
    qCon: [],
    qNox: [],
    BSR: [],
    EMG: [],
    timestamps: [],
  }); // Real-time trend data
  const [dsaSpectrogram, setDsaSpectrogram] = useState(null); // DSA spectrogram matrix: Float32Array
  const [dsaStartTime, setDsaStartTime] = useState(null); // Start time for DSA (HH:MM format)
  const genRef = useRef(null);
  const bufferRef = useRef([]);
  const rafRef = useRef(null);
  const trendBufferRef = useRef({
    qCon: [],
    qNox: [],
    BSR: [],
    EMG: [],
  }); // Buffer to accumulate samples for 10-second aggregation
  const trendIntervalRef = useRef(null);
  const startTimeRef = useRef(Date.now()); // Track when trend collection started
  const sweepStartTimeRef = useRef(null); // Track when current sweep started (absolute timestamp)
  const packetArrivalRef = useRef({ count: 0, firstPacketTime: null, lastPacketTime: null }); // Track packet arrival for timestamp validation
  const expectedFsHz = 1024.0; // Expected sampling frequency: 1024 Hz
  const samplesPerPacket = 256; // Samples per packet
  const expectedPacketsPerSecond = 4; // Expected: 4 packets/second
  const expectedTimePerPacket = samplesPerPacket / expectedFsHz; // 256/1024 = 0.25 seconds per packet
  
  // DSA spectrogram state
  const dsaBufferRef = useRef([]); // Buffer to accumulate EEG samples for 1-second windows
  const dsaSpectraBufferRef = useRef([]); // Buffer to store computed 1-second spectra (keep last 10-15)
  const dsaColumnCountRef = useRef(0); // Number of columns added (max 360 for 1 hour)
  const dsaComputeIntervalRef = useRef(null); // Interval for computing FFT every 1 second
  const dsaDisplayIntervalRef = useRef(null); // Interval for displaying averaged column every 10 seconds
  const dsaStartTimeRef = useRef(null); // Absolute start time for DSA
  const MAX_DSA_COLUMNS = 360; // 1 hour = 3600 seconds / 10 seconds per column
  const DSA_COMPUTE_INTERVAL_SECONDS = 1; // Compute FFT every 1 second
  const DSA_DISPLAY_INTERVAL_SECONDS = 10; // Display averaged column every 10 seconds
  const DSA_AVERAGE_COUNT = 10; // Average last 10 spectra for display
  const DSA_FREQ_MAX = 100; // Maximum frequency in Hz

  // Compute FFT spectrogram from 1 second of EEG samples (1024 samples at 1024 Hz)
  const computeSpectrogramColumn = (samples) => {
    if (!samples || samples.length === 0) return null;
    
    const fs = expectedFsHz;
    const samplesNeeded = fs * DSA_COMPUTE_INTERVAL_SECONDS; // 1024 samples for 1 second
    
    // Use the most recent samples (should be exactly samplesNeeded, but handle edge cases)
    const samplesToUse = samples.slice(-samplesNeeded);
    
    if (samplesToUse.length < samplesNeeded * 0.8) {
      // Not enough samples yet
      return null;
    }
    
    // Use window size of 1024 (1 second) - power of 2 for efficient FFT
    const windowSize = 1024;
    const freqBins = DSA_FREQ_MAX + 1;
    
    // Apply Hanning window
    const windowed = new Float32Array(windowSize);
    for (let j = 0; j < windowSize && j < samplesToUse.length; j++) {
      const hanning = 0.5 * (1 - Math.cos(2 * Math.PI * j / (windowSize - 1)));
      windowed[j] = samplesToUse[j] * hanning;
    }
    
    // Pad to windowSize if needed
    for (let j = samplesToUse.length; j < windowSize; j++) {
      const hanning = 0.5 * (1 - Math.cos(2 * Math.PI * j / (windowSize - 1)));
      windowed[j] = 0; // Zero padding
    }
    
    // Compute FFT (returns phasors: complex numbers)
    const phasors = fft(windowed);
    
    // Get magnitudes from phasors
    const magnitudes = fftUtil.fftMag(phasors);
    
    // Convert to power spectrum
    const powerSpectrum = new Float32Array(freqBins);
    for (let k = 0; k < freqBins && k < magnitudes.length; k++) {
      // Frequency bin k corresponds to frequency k * fs / windowSize
      const freq = (k * fs) / windowSize;
      if (freq <= DSA_FREQ_MAX) {
        // Power is magnitude squared, normalized by window size
        powerSpectrum[k] = (magnitudes[k] * magnitudes[k]) / windowSize;
      }
    }
    
    // Return unnormalized power spectrum (normalization will be done when averaging)
    return powerSpectrum;
  };
  
  // Average multiple power spectra
  const averagePowerSpectra = (spectraArray) => {
    if (!spectraArray || spectraArray.length === 0) return null;
    
    const freqBins = DSA_FREQ_MAX + 1;
    const averaged = new Float32Array(freqBins);
    averaged.fill(0);
    
    // Sum all spectra
    for (const spectrum of spectraArray) {
      if (spectrum && spectrum.length === freqBins) {
        for (let k = 0; k < freqBins; k++) {
          averaged[k] += spectrum[k];
        }
      }
    }
    
    // Average
    const count = spectraArray.length;
    if (count > 0) {
      for (let k = 0; k < freqBins; k++) {
        averaged[k] /= count;
      }
    }
    
    // Normalize to 0-1 range using log scale (typical for spectrograms)
    let maxPower = 0;
    for (let k = 0; k < freqBins; k++) {
      if (averaged[k] > maxPower) {
        maxPower = averaged[k];
      }
    }
    
    const normalized = new Float32Array(freqBins);
    if (maxPower > 0) {
      for (let k = 0; k < freqBins; k++) {
        // Log scale normalization
        const logPower = Math.log10(averaged[k] + 1e-10); // Add small epsilon to avoid log(0)
        const logMax = Math.log10(maxPower + 1e-10);
        normalized[k] = logMax > 0 ? logPower / logMax : 0;
        // Clamp to 0-1
        normalized[k] = Math.max(0, Math.min(1, normalized[k]));
      }
    }
    
    return normalized;
  };

  // Generate time labels every 10 minutes from now
  const generateTimeLabels = () => {
    const labels = [];
    const now = new Date();
    const startTime = new Date(now);
    
    // Round down to the nearest 10-minute mark
    const currentMinutes = now.getMinutes();
    const roundedMinutes = Math.floor(currentMinutes / 10) * 10;
    startTime.setMinutes(roundedMinutes, 0, 0);
    
    // Generate labels for the next 40 minutes (every 10 minutes)
    for (let i = 0; i <= 4; i++) {
      const time = new Date(startTime);
      time.setMinutes(startTime.getMinutes() + i * 10);
      const hours = time.getHours();
      const minutes = time.getMinutes();
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      labels.push(timeString);
    }
    
    return labels;
  };

  // Subscribe to WebSocket connection state changes
  useEffect(() => {
    const unsubscribeState = eegService.subscribeToConnectionState((state) => {
      setWsConnectionState(state);
    });

    return () => {
      unsubscribeState();
    };
  }, []);

  // Subscribe to real EEG stream from Bun on page load
  useEffect(() => {
    const unsubscribe = eegService.subscribe((msg) => {
      // Basic log so you can verify data arrival in the browser console
      // msg shape: { type: "snapshot" | "eeg", data: { ... } }
      // eslint-disable-next-line no-console
      console.log("[Connect] EEG message received:", msg.type, "qCON:", msg.data?.qCON, "SQI:", msg.data?.SQI);

      // Process both "snapshot" and "eeg" message types
      if (msg && msg.data) {
        const decoded = decodeEegMessage(msg);
        // Force a new object reference to ensure React detects the change
        setLastEegPacket({ ...decoded });
        // eslint-disable-next-line no-console
        console.log("[Connect] Setting new EEG packet state:", decoded);
        
        // Accumulate samples for trend aggregation (4 samples per second)
        // We collect samples and aggregate every 10 seconds (40 samples)
        if (
          decoded.qCON !== null && decoded.qCON !== undefined &&
          decoded.qNOX !== null && decoded.qNOX !== undefined &&
          decoded.BSR !== null && decoded.BSR !== undefined &&
          decoded.EMG !== null && decoded.EMG !== undefined
        ) {
          trendBufferRef.current.qCon.push(decoded.qCON);
          trendBufferRef.current.qNox.push(decoded.qNOX);
          trendBufferRef.current.BSR.push(decoded.BSR);
          trendBufferRef.current.EMG.push(decoded.EMG);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Aggregate trend data every 10 seconds (40 samples at 4 samples/sec)
  useEffect(() => {
    startTimeRef.current = Date.now();
    
    trendIntervalRef.current = setInterval(() => {
      const buffers = trendBufferRef.current;
      
      // Calculate means for each parameter (40 samples = 10 seconds at 4 samples/sec)
      const calculateMean = (arr) => {
        if (arr.length === 0) return null;
        const sum = arr.reduce((a, b) => a + b, 0);
        return Math.round(sum / arr.length);
      };
      
      const qConMean = calculateMean(buffers.qCon);
      const qNoxMean = calculateMean(buffers.qNox);
      const BSRMean = calculateMean(buffers.BSR);
      const EMGMean = calculateMean(buffers.EMG);
      
      // Only add if we have valid data
      if (qConMean !== null && qNoxMean !== null && BSRMean !== null && EMGMean !== null) {
        setTrendData((prev) => {
          const newData = {
            qCon: [...prev.qCon, qConMean],
            qNox: [...prev.qNox, qNoxMean],
            BSR: [...prev.BSR, BSRMean],
            EMG: [...prev.EMG, EMGMean],
            timestamps: [...prev.timestamps, Date.now()],
          };
          
          // Keep only last 40 minutes of data (240 data points max: 40 min * 6 points/min)
          const maxPoints = 240;
          if (newData.qCon.length > maxPoints) {
            const removeCount = newData.qCon.length - maxPoints;
            newData.qCon = newData.qCon.slice(removeCount);
            newData.qNox = newData.qNox.slice(removeCount);
            newData.BSR = newData.BSR.slice(removeCount);
            newData.EMG = newData.EMG.slice(removeCount);
            newData.timestamps = newData.timestamps.slice(removeCount);
          }
          
          return newData;
        });
      }
      
      // Clear buffers for next 10-second period
      buffers.qCon = [];
      buffers.qNox = [];
      buffers.BSR = [];
      buffers.EMG = [];
    }, 10000); // Every 10 seconds
    
    return () => {
      if (trendIntervalRef.current) {
        clearInterval(trendIntervalRef.current);
      }
    };
  }, []);

  // Debug: Log when lastEegPacket state changes
  useEffect(() => {
    if (lastEegPacket) {
      // eslint-disable-next-line no-console
      console.log("[Connect] lastEegPacket state updated:", lastEegPacket);
    }
  }, [lastEegPacket]);

  // Real EEG waveform data from WebSocket stream
  // Sweep-based display: builds up signal over N seconds, then resets and repeats
  // Backend sends: 256 samples per packet with eeg_sample_times (timestamps) and eeg_samples_uv (unclamped µV values)
  // Downsampled to ~256 Hz (every 4th sample) to reduce point count for Chart.js performance
  useEffect(() => {
    // Time-based buffer: array of {x: timestamp, y: value} points (absolute timestamps)
    bufferRef.current = [];
    // Initialize sweep start time
    sweepStartTimeRef.current = null;
    // Reset packet tracking
    packetArrivalRef.current = { count: 0, firstPacketTime: null, lastPacketTime: null };

    // Function to add new samples to the buffer with their timestamps
    // Samples are in µV (from eeg_samples_uv, unclamped) - visual clamping done by Chart.js y-axis
    // Downsample to reduce point count: keep every 4th sample (~256 Hz effective from ~1024 Hz)
    // Recalculate timestamps based on expected sample rate: 4 packets/sec × 256 samples = 1024 Hz
    const addSamplesToBuffer = (samples, sampleTimes) => {
      if (!Array.isArray(samples) || samples.length === 0) return;
      if (!Array.isArray(sampleTimes) || sampleTimes.length !== samples.length) return;

      const now = Date.now() / 1000; // Current time in seconds
      const packetInfo = packetArrivalRef.current;
      
      // Track packet arrival
      if (packetInfo.firstPacketTime === null) {
        packetInfo.firstPacketTime = now;
        packetInfo.lastPacketTime = now;
        packetInfo.count = 0;
      }
      packetInfo.count++;
      packetInfo.lastPacketTime = now;
      
      // Calculate expected timestamp for this packet based on arrival time
      // Expected: 4 packets/second, so packet N should be at time = firstPacketTime + (N-1) * 0.25
      const packetIndex = packetInfo.count - 1;
      const expectedPacketStartTime = packetInfo.firstPacketTime + (packetIndex * expectedTimePerPacket);
      const expectedPacketEndTime = expectedPacketStartTime + expectedTimePerPacket;
      
      // Recalculate sample timestamps based on expected timing
      // Each packet has 256 samples covering 0.25 seconds (256/1024 Hz)
      const dt = 1.0 / expectedFsHz; // Time per sample: 1/1024 seconds
      const recalculatedSampleTimes = [];
      for (let i = 0; i < samples.length; i++) {
        // Calculate timestamp: packet start + sample index * dt
        recalculatedSampleTimes.push(expectedPacketStartTime + (i * dt));
      }

      const DOWNSAMPLE_FACTOR = 4; // Keep every 4th sample
      
      // Downsample: add every 4th sample with recalculated timestamps
      for (let i = 0; i < samples.length; i += DOWNSAMPLE_FACTOR) {
        bufferRef.current.push({ x: recalculatedSampleTimes[i], y: samples[i] });
      }
    
      // Keep buffer size sane: only keep last ~30 seconds (enough for multiple sweeps)
      if (bufferRef.current.length > 0) {
        const latestT = bufferRef.current[bufferRef.current.length - 1].x;
        const cutoffT = latestT - 30;
        bufferRef.current = bufferRef.current.filter((p) => p.x >= cutoffT);
      }
    };
    

    // Subscribe to EEG data updates
    // Use eeg_samples_uv and eeg_sample_times from the new decoder
    const unsubscribe = eegService.subscribe((msg) => {
      // Process both "snapshot" and "eeg" message types
      if (
        msg &&
        msg.data &&
        Array.isArray(msg.data.eeg_samples_uv) &&
        msg.data.eeg_samples_uv.length > 0 &&
        Array.isArray(msg.data.eeg_sample_times) &&
        msg.data.eeg_sample_times.length === msg.data.eeg_samples_uv.length
      ) {
        addSamplesToBuffer(msg.data.eeg_samples_uv, msg.data.eeg_sample_times);
      }
    });

    // Smooth animation frame update for sweep-based visualization
    const tick = () => {
      const data = bufferRef.current;
    
      if (data.length > 0) {
        const latestT = data[data.length - 1].x; // Latest absolute timestamp from stream
        
        // Initialize sweep start time on first data point
        if (sweepStartTimeRef.current === null) {
          sweepStartTimeRef.current = latestT;
        }
        
        // Calculate elapsed time since sweep started
        const elapsedTime = latestT - sweepStartTimeRef.current;
        
        // If sweep duration exceeded, reset to start a new sweep
        // Handle case where multiple sweep periods may have passed
        if (elapsedTime >= sweepDurationSeconds) {
          // Calculate how many complete sweeps have passed
          const completedSweeps = Math.floor(elapsedTime / sweepDurationSeconds);
          // Start new sweep at the beginning of the current period
          sweepStartTimeRef.current = sweepStartTimeRef.current + (completedSweeps * sweepDurationSeconds);
        }
        
        // Get data points within the current sweep window
        const sweepStartT = sweepStartTimeRef.current;
        const sweepEndT = sweepStartT + sweepDurationSeconds;
        
        // Filter data points that fall within the current sweep
        const sweepData = data.filter((p) => p.x >= sweepStartT && p.x <= sweepEndT);
        
        // Transform to relative time (0 to sweepDurationSeconds)
        const visibleData = sweepData.map((p) => ({
          x: p.x - sweepStartT, // Relative time: 0 to sweepDurationSeconds
          y: p.y,
        }));
        
        setWaveformData(visibleData);
      } else {
        setWaveformData([]);
        sweepStartTimeRef.current = null;
      }
    
      rafRef.current = requestAnimationFrame(tick);
    };
    

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      unsubscribe();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      sweepStartTimeRef.current = null;
    };
  }, [sweepDurationSeconds]); // Re-run when sweep duration changes

  // DSA Spectrogram: Compute FFT every 1s, display averaged column every 10s
  useEffect(() => {
    dsaBufferRef.current = [];
    dsaSpectraBufferRef.current = [];
    dsaColumnCountRef.current = 0;
    
    // Initialize DSA spectrogram matrix: 360 columns (1 hour) × 101 frequency bins (0-100 Hz)
    const freqBins = DSA_FREQ_MAX + 1;
    const initialMatrix = new Float32Array(MAX_DSA_COLUMNS * freqBins);
    initialMatrix.fill(NaN);
    setDsaSpectrogram(initialMatrix);
    
    // Set start time when first data arrives
    const getCurrentTimeString = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };
    
    // Function to add samples to DSA buffer
    const addSamplesToDsaBuffer = (samples) => {
      if (!Array.isArray(samples) || samples.length === 0) return;
      
      // Add all samples to buffer (no downsampling for spectrogram)
      dsaBufferRef.current.push(...samples);
      
      // Initialize start time on first sample
      if (dsaStartTimeRef.current === null) {
        dsaStartTimeRef.current = Date.now() / 1000; // Absolute timestamp in seconds
        setDsaStartTime(getCurrentTimeString());
      }
      
      // Keep buffer size: need at least 15 seconds worth (15 * 1024 = 15,360 samples)
      // Keep more than needed to ensure we always have enough for computation
      const maxBufferSize = 15 * expectedFsHz; // 15 seconds
      if (dsaBufferRef.current.length > maxBufferSize) {
        // Remove oldest samples, keep the most recent
        const removeCount = dsaBufferRef.current.length - maxBufferSize;
        dsaBufferRef.current = dsaBufferRef.current.slice(removeCount);
      }
    };
    
    // Function to compute FFT every 1 second
    const computeSpectrum = () => {
      const samplesNeeded = expectedFsHz * DSA_COMPUTE_INTERVAL_SECONDS; // 1024 samples for 1 second
      const availableSamples = dsaBufferRef.current.length;
      
      if (availableSamples >= samplesNeeded * 0.8) { // At least 80% of needed samples
        // Use the most recent samples
        const samplesToUse = dsaBufferRef.current.slice(-samplesNeeded);
        
        // Compute power spectrum for 1 second
        const spectrum = computeSpectrogramColumn(samplesToUse);
        
        if (spectrum) {
          // Add to spectra buffer
          dsaSpectraBufferRef.current.push(spectrum);
          
          // Keep only last 15 spectra (more than needed for averaging)
          if (dsaSpectraBufferRef.current.length > 15) {
            dsaSpectraBufferRef.current.shift(); // Remove oldest
          }
        }
      }
    };
    
    // Function to display averaged column every 10 seconds
    const displayAveragedColumn = () => {
      const freqBins = DSA_FREQ_MAX + 1;
      
      if (dsaColumnCountRef.current >= MAX_DSA_COLUMNS) {
        // Shift left: remove oldest column, add new one at the end
        setDsaSpectrogram((prevMatrix) => {
          if (!prevMatrix) return prevMatrix;
          
          const newMatrix = new Float32Array(MAX_DSA_COLUMNS * freqBins);
          // Copy columns 1-359 to positions 0-358
          for (let col = 1; col < MAX_DSA_COLUMNS; col++) {
            for (let freq = 0; freq < freqBins; freq++) {
              newMatrix[(col - 1) * freqBins + freq] = prevMatrix[col * freqBins + freq];
            }
          }
          // New column will be added at position 359
          for (let freq = 0; freq < freqBins; freq++) {
            newMatrix[(MAX_DSA_COLUMNS - 1) * freqBins + freq] = NaN; // Will be filled below
          }
          
          return newMatrix;
        });
        dsaColumnCountRef.current = MAX_DSA_COLUMNS - 1; // Will be incremented below
      }
      
      // Get last 10 spectra from buffer
      const spectraToAverage = dsaSpectraBufferRef.current.slice(-DSA_AVERAGE_COUNT);
      
      if (spectraToAverage.length >= DSA_AVERAGE_COUNT) {
        // Average the last 10 spectra
        const averagedColumn = averagePowerSpectra(spectraToAverage);
        
        if (averagedColumn) {
          setDsaSpectrogram((prevMatrix) => {
            if (!prevMatrix) {
              const newMatrix = new Float32Array(MAX_DSA_COLUMNS * freqBins);
              newMatrix.fill(NaN);
              // Add first column
              for (let freq = 0; freq < freqBins; freq++) {
                newMatrix[freq] = averagedColumn[freq];
              }
              return newMatrix;
            }
            
            const newMatrix = new Float32Array(prevMatrix);
            const colIndex = dsaColumnCountRef.current;
            
            // Add new averaged column
            for (let freq = 0; freq < freqBins; freq++) {
              newMatrix[colIndex * freqBins + freq] = averagedColumn[freq];
            }
            
            return newMatrix;
          });
          
          dsaColumnCountRef.current++;
        }
      }
    };
    
    // Subscribe to EEG data for DSA
    const unsubscribeDsa = eegService.subscribe((msg) => {
      if (
        msg &&
        msg.data &&
        Array.isArray(msg.data.eeg_samples_uv) &&
        msg.data.eeg_samples_uv.length > 0
      ) {
        addSamplesToDsaBuffer(msg.data.eeg_samples_uv);
      }
    });
    
    // Compute FFT every 1 second
    dsaComputeIntervalRef.current = setInterval(() => {
      computeSpectrum();
    }, DSA_COMPUTE_INTERVAL_SECONDS * 1000); // 1 second
    
    // Display averaged column every 10 seconds
    dsaDisplayIntervalRef.current = setInterval(() => {
      displayAveragedColumn();
    }, DSA_DISPLAY_INTERVAL_SECONDS * 1000); // 10 seconds
    
    return () => {
      unsubscribeDsa();
      if (dsaComputeIntervalRef.current) {
        clearInterval(dsaComputeIntervalRef.current);
      }
      if (dsaDisplayIntervalRef.current) {
        clearInterval(dsaDisplayIntervalRef.current);
      }
      dsaBufferRef.current = [];
      dsaSpectraBufferRef.current = [];
      dsaColumnCountRef.current = 0;
      dsaStartTimeRef.current = null;
    };
  }, []); // Run once on mount

  // Waveform chart configuration
  // Use time-based data: {x: timestamp, y: value}
  const waveformChartData = {
    datasets: [
      {
        label: "EEG Signal (µV)",
        data: waveformData,
        borderColor: "#ffffff",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1.5,
        pointRadius: 0,
        fill: false,
        tension: 0.1,
      },
    ],
  };

  const waveformChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        type: "linear",
        position: "bottom",
        display: true,
        title: {
          display: true,
          text: "Time (seconds)",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          font: {
            size: 10,
          },
          // Show time within sweep (0 to sweepDurationSeconds)
          callback: function(value) {
            return `${value.toFixed(1)}s`;
          },
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        // Set time range to show 0 to sweepDurationSeconds
        min: 0,
        max: sweepDurationSeconds,
      },
      y: {
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Amplitude (µV)",
          color: "#ffffff",
        },
        // Visual clamping: data is unclamped, but y-axis limits provide display bounds
        min: -120,
        max: 120,
        ticks: {
          color: "#ffffff",
          font: {
            size: 10,
          },
          stepSize: 120,
          callback: function(value) {
            if (value === 0) return "0";
            if (value === 120) return "120µV";
            if (value === -120) return "-120µV";
            return "";
          },
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  }), [sweepDurationSeconds]);

  // Generate time labels for x-axis (every 10 minutes from now)
  const trendTimeLabels = useMemo(() => generateTimeLabels(), []);

  // Convert timestamps to time-based x-axis labels
  // Data points come every 10 seconds, but we label x-axis every 10 minutes
  const trendChartData = useMemo(() => {
    const { qCon, qNox, BSR, EMG, timestamps } = trendData;
    
    if (timestamps.length === 0) {
      // Return empty data structure with time labels
      return {
        labels: trendTimeLabels,
        datasets: [
        {
          label: "qCON",
          data: [],
          borderColor: "#ffffff",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        {
          label: "qNOX",
          data: [],
          borderColor: "#ffeb3b",
          backgroundColor: "rgba(255, 235, 59, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        {
          label: "BSR",
          data: [],
          borderColor: "#f44336",
          backgroundColor: "rgba(244, 67, 54, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        {
          label: "EMG",
          data: [],
          borderColor: "#64b5f6",
          backgroundColor: "rgba(100, 181, 246, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        ],
      };
    }
    
    // Convert timestamps to time strings (HH:MM format)
    // Use time-based x-axis: each data point has its timestamp converted to time string
    const timeLabels = timestamps.map((ts) => {
      const date = new Date(ts);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    });
    
    // Create data points with time labels
    const dataPoints = timestamps.map((ts, index) => ({
      x: ts, // Use timestamp as x value for proper scaling
      qCon: qCon[index],
      qNox: qNox[index],
      BSR: BSR[index],
      EMG: EMG[index],
    }));
    
    return {
      datasets: [
        {
          label: "qCON",
          data: dataPoints.map((p) => ({ x: p.x, y: p.qCon })),
          borderColor: "#ffffff", // White
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        {
          label: "qNOX",
          data: dataPoints.map((p) => ({ x: p.x, y: p.qNox })),
          borderColor: "#ffeb3b", // Yellow
          backgroundColor: "rgba(255, 235, 59, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        {
          label: "BSR",
          data: dataPoints.map((p) => ({ x: p.x, y: p.BSR })),
          borderColor: "#f44336", // Red
          backgroundColor: "rgba(244, 67, 54, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        {
          label: "EMG",
          data: dataPoints.map((p) => ({ x: p.x, y: p.EMG })),
          borderColor: "#64b5f6", // Blue
          backgroundColor: "rgba(100, 181, 246, 0.1)",
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
      ],
    };
  }, [trendData, trendTimeLabels]);

  const trendChartOptions = useMemo(() => {
    const now = Date.now();
    const startTime = new Date(now);
    const currentMinutes = new Date(now).getMinutes();
    const roundedMinutes = Math.floor(currentMinutes / 10) * 10;
    startTime.setMinutes(roundedMinutes, 0, 0);
    
    // Calculate time range: from rounded time to 40 minutes ahead
    let minTime = startTime.getTime();
    let maxTime = minTime + 40 * 60 * 1000; // 40 minutes in milliseconds
    
    // If we have data, adjust range to show from first data point
    if (trendData.timestamps.length > 0) {
      const firstTimestamp = Math.min(...trendData.timestamps);
      const lastTimestamp = Math.max(...trendData.timestamps);
      // Round down first timestamp to nearest 10-minute mark
      const firstDate = new Date(firstTimestamp);
      const firstMinutes = firstDate.getMinutes();
      const roundedFirstMinutes = Math.floor(firstMinutes / 10) * 10;
      firstDate.setMinutes(roundedFirstMinutes, 0, 0);
      minTime = firstDate.getTime();
      // Extend max time to show at least 40 minutes from start
      maxTime = Math.max(maxTime, minTime + 40 * 60 * 1000);
    }
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disable animations - data appears instantly
      transitions: {
        active: {
          animation: {
            duration: 0, // No animation duration
          },
        },
      },
      plugins: {
        legend: {
          display: false, // Disable Chart.js legend - using custom legend instead
        },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#ffffff",
          bodyColor: "#ffffff",
          borderColor: "rgba(255, 255, 255, 0.2)",
          borderWidth: 1,
          callbacks: {
            title: function(context) {
              const timestamp = context[0].parsed.x;
              const date = new Date(timestamp);
              const hours = date.getHours();
              const minutes = date.getMinutes();
              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          position: "bottom",
          display: true,
          min: minTime,
          max: maxTime,
          ticks: {
            color: "#ffffff",
            font: {
              size: 10,
            },
            stepSize: 10 * 60 * 1000, // 10 minutes in milliseconds
            callback: function(value) {
              // Only show labels at 10-minute intervals
              const date = new Date(value);
              const minutes = date.getMinutes();
              // Show label if minutes are divisible by 10
              if (minutes % 10 === 0) {
                const hours = date.getHours();
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
              }
              return "";
            },
          },
          grid: {
            color: "rgba(255, 255, 255, 0.1)",
          },
        },
        y: {
          display: true,
          min: 0,
          max: 100,
          ticks: {
            color: "#ffffff",
            font: {
              size: 10,
            },
            stepSize: 20,
            callback: function(value) {
              if (value === 0 || value === 20 || value === 40 || value === 60 || value === 80 || value === 100) {
                return value.toString();
              }
              return "";
            },
          },
          grid: {
            color: "rgba(255, 255, 255, 0.1)",
            borderDash: [5, 5],
          },
        },
      },
    };
  }, [trendData]);

  return (
    <DashboardLayout>
      <MDBox
        pt={3}
        pb={3}
        px={{ xs: 2, md: 3 }}
        sx={{
          backgroundColor: contentBg,
          color: contentColor,
          minHeight: "calc(100vh - 72px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Connect-specific bar: device selection + WebSocket status */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <MDButton
            variant="contained"
            color="info"
            onClick={() => navigate("/device-selection")}
            sx={{ minWidth: "150px" }}
          >
            {t("connect.backToDeviceSelection")}
          </MDButton>
          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* WebSocket Connection Status Indicator */}
            {wsConnectionState === "connecting" && (
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "0.5rem 1rem",
                  backgroundColor: "rgba(255, 193, 7, 0.2)",
                  borderRadius: "4px",
                  border: "1px solid rgba(255, 193, 7, 0.5)",
                }}
              >
                <CircularProgress size={16} sx={{ color: "#ffc107" }} />
                <MDTypography variant="body2" color="#ffc107" sx={{ fontSize: "0.875rem" }}>
                  {t("connect.connecting")}
                </MDTypography>
              </MDBox>
            )}
            {wsConnectionState === "connected" && (
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "0.5rem 1rem",
                  backgroundColor: "rgba(76, 175, 80, 0.2)",
                  borderRadius: "4px",
                  border: "1px solid rgba(76, 175, 80, 0.5)",
                }}
              >
                <MDBox
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#4caf50",
                  }}
                />
                <MDTypography variant="body2" color="#4caf50" sx={{ fontSize: "0.875rem" }}>
                  {t("connect.connected")}
                </MDTypography>
              </MDBox>
            )}
            {wsConnectionState === "disconnected" && (
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "0.5rem 1rem",
                  backgroundColor: "rgba(244, 67, 54, 0.2)",
                  borderRadius: "4px",
                  border: "1px solid rgba(244, 67, 54, 0.5)",
                }}
              >
                <MDBox
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#f44336",
                  }}
                />
                <MDTypography variant="body2" color="#f44336" sx={{ fontSize: "0.875rem" }}>
                  {t("connect.disconnected")}
                </MDTypography>
              </MDBox>
            )}
            <MDTypography variant="h6" color="white" sx={{ fontWeight: "bold" }}>
              {t("connect.freseniusKabi")}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Top Row Parameters */}
        <MDBox display="flex" gap={1} mb={2} flexWrap="wrap">
          <ParameterCard
            label="SQI"
            value={lastEegPacket?.SQI !== null && lastEegPacket?.SQI !== undefined ? String(Math.round(lastEegPacket.SQI)) : "---"}
            color="#4caf50"
            textColor="black"
          />
          <ParameterCard
            label="EMG"
            value={lastEegPacket?.EMG !== null && lastEegPacket?.EMG !== undefined ? String(Math.round(lastEegPacket.EMG)) : "---"}
            color="#64b5f6"
            textColor="black"
          />
          <ParameterCard
            label="BSR"
            value={lastEegPacket?.BSR !== null && lastEegPacket?.BSR !== undefined ? String(Math.round(lastEegPacket.BSR)) : "---"}
            color="#f44336"
            textColor="black"
          />
          <ParameterCard
            label="qNOX"
            value={lastEegPacket?.qNOX !== null && lastEegPacket?.qNOX !== undefined ? String(Math.round(lastEegPacket.qNOX)) : "---"}
            color="#ffeb3b"
            textColor="black"
          />
          <ParameterCard
            label="qCON"
            value={lastEegPacket?.qCON !== null && lastEegPacket?.qCON !== undefined ? String(Math.round(lastEegPacket.qCON)) : "---"}
            color="#ffffff"
            textColor="black"
          />
        </MDBox>

        <Grid container spacing={2}>
          {/* Left Column - Graphs */}
          <Grid item xs={12} md={8}>
            {/* Main Waveform Graph */}
            <MDBox mb={2}>
              <MDBox
                sx={{
                  backgroundColor: deviceBackground,
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "4px",
                  padding: "1rem",
                  height: "300px",
                  position: "relative",
                }}
              >
                {/* Sweep Duration Selector */}
                <MDBox
                  sx={{
                    position: "absolute",
                    top: "1rem",
                    right: "1rem",
                    zIndex: 10,
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: 120,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "4px",
                    }}
                  >
                    <InputLabel
                      id="sweep-duration-label"
                      sx={{ color: "white" }}
                    >
                      Sweep
                    </InputLabel>
                    <Select
                      labelId="sweep-duration-label"
                      id="sweep-duration-select"
                      value={sweepDurationSeconds}
                      label="Sweep"
                      onChange={(e) => setSweepDurationSeconds(e.target.value)}
                      sx={{
                        color: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255, 255, 255, 0.3)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255, 255, 255, 0.5)",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "white",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: "#1a1a1a",
                            color: "white",
                            "& .MuiMenuItem-root": {
                              color: "white",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value={5}>5 sec</MenuItem>
                      <MenuItem value={10}>10 sec</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <Line data={waveformChartData} options={waveformChartOptions} />
              </MDBox>
            </MDBox>
            {/* DSA Graph */}
            <MDBox mb={2}>
              <MDBox
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 0,
                }}
              >
                {/* Graph Container */}
                <MDBox
                  sx={{
                    flex: 1,
                    backgroundColor: deviceBackground,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRight: "none",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    padding: "0rem",
                    height: "220px",
                  }}
                >
                  <DSA
                    startTime={dsaStartTime || "00:00"}
                    freqMax={DSA_FREQ_MAX}
                    height={210}
                    title=""
                    dynamicMode={true}
                    spectrogramData={dsaSpectrogram}
                    timeBins={MAX_DSA_COLUMNS}
                    background={deviceBackground}
                  />
                </MDBox>
                
                {/* Custom Legend */}
                <MDBox
                  sx={{
                    backgroundColor: deviceBackground,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderLeft: "none",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                    padding: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    minWidth: "100px",
                    height: "220px",
                  }}
                >
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#ffffff",
                      fontSize: "14px",
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    Power
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "#ffffff",
                      fontSize: "12px",
                      opacity: 0.8,
                      mb: 0.5,
                    }}
                  >
                    High
                  </MDTypography>
                  <MDBox
                    sx={{
                      width: "20px",
                      height: "120px",
                      borderRadius: "2px",
                      overflow: "hidden",
                      border: "1px solid rgba(255,255,255,0.2)",
                      background:
                        "linear-gradient(to bottom, rgb(255,0,0), rgb(255,140,0), rgb(255,220,0), rgb(0,255,120), rgb(0,200,255), rgb(0,60,160))",
                      mb: 0.5,
                    }}
                  />
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "#ffffff",
                      fontSize: "12px",
                      opacity: 0.8,
                    }}
                  >
                    Low
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
            {/* Trend Graph */}
            <MDBox>
              <MDBox
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 0,
                }}
              >
                {/* Graph Container */}
                <MDBox
                  sx={{
                    flex: 1,
                    backgroundColor: deviceBackground,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRight: "none",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    padding: "1rem",
                    height: "200px",
                  }}
                >
                  <Line data={trendChartData} options={trendChartOptions} />
                </MDBox>
                
                {/* Custom Legend */}
                <MDBox
                  sx={{
                    backgroundColor: deviceBackground,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderLeft: "none",
                    borderTopRightRadius: "4px",
                    borderBottomRightRadius: "4px",
                    padding: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    minWidth: "100px",
                    height: "200px",
                  }}
                >
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#ffffff",
                      fontSize: "18px",
                      fontWeight: "bold",
                      mb: 1.5,
                    }}
                  >
                    qCON
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#ffeb3b",
                      fontSize: "18px",
                      fontWeight: "bold",
                      mb: 1.5,
                    }}
                  >
                    qNOX
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#f44336",
                      fontSize: "18px",
                      fontWeight: "bold",
                      mb: 1.5,
                    }}
                  >
                    BSR
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#64b5f6",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    EMG
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>

          {/* Right Column - AI Feedback Panel */}
          <Grid item xs={12} md={4}>
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
              }}
            >
              {/* AI Feedback Window */}
              <MDBox
                sx={{
                  backgroundColor: deviceBackground,
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "4px",
                  padding: "1.5rem",
                  minHeight: "300px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <MDTypography
                  variant="h6"
                  color="white"
                  sx={{ fontWeight: "bold", mb: 1.5 }}
                >
                  AI Analysis
                </MDTypography>
                <MDBox
                  sx={{
                    flex: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "4px",
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                    paddingTop: "3rem",
                    paddingBottom: "3rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MDTypography
                    variant="body1"
                    color="white"
                    sx={{
                      textAlign: "center",
                      fontSize: "1.1rem",
                      lineHeight: 1.6,
                    }}
                  >
                    Patient is awake.
                  </MDTypography>
                </MDBox>
              

              {/* Action Buttons */}
              <MDBox
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "1rem",
                  gap: 1.5,
                }}
              >
                <MDButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    minHeight: "50px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    textTransform: "none",
                  }}
                >
                  Start Dosing
                </MDButton>
                <MDButton
                  variant="contained"
                  color="success"
                  fullWidth
                  sx={{
                    minHeight: "50px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    textTransform: "none",
                  }}
                >
                  Keep Awake
                </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>

        {/* Bottom Branding */}
        <MDBox
          textAlign="center"
          mt={2}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <MDTypography
            variant="h4"
            fontWeight="bold"
            color="white"
            sx={{ fontSize: "2rem" }}
          >
            con
          </MDTypography>
          <MDTypography
            variant="h4"
            fontWeight="bold"
            color="warning"
            sx={{ fontSize: "2rem" }}
          >
            ox
          </MDTypography>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default Connect;
